import { useState, ChangeEvent, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import Banner from "../../../components/Banner";
import axios from "axios";
import toast from "react-hot-toast";
import LoadingSpinner from "../../../components/elements/LoadingSpinner";

interface Errors {
  firstName?: string;
  lastName?: string;
  staffId?: string;
  email?: string;
  confirmPassword?: string;
  password?: string;
}

const AdminSignup = () => {
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState({
    firstName: "",
    lastName: "",
    email: "",
    staffId: "",
    password: "",
    confirmPassword: "",
  });
  const navigate = useNavigate();

  const validateForm = () => {
    let isValid = true;
    const newErrors: Errors = {};

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inputValue.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d).{8,}$/;
    if (!passwordRegex.test(inputValue.password)) {
      newErrors.password =
        "Password must be at least 8 characters and contain both letters and numbers";
      isValid = false;
    }

    if (inputValue.password !== inputValue.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      isValid = false;
    }

    if (!inputValue.firstName) {
      newErrors.firstName = "Please enter your first name";
      isValid = false;
    }

    if (!inputValue.lastName) {
      newErrors.lastName = "Please enter your last name";
      isValid = false;
    }

    if (!inputValue.staffId) {
      newErrors.staffId = "Please enter your staff ID";
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value.trim() !== "") {
      setErrors({
        ...errors,
        [name]: undefined,
      });
    }
    setInputValue((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleCreateAccount = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);
      const payload = {
        firstName: inputValue.firstName,
        lastName: inputValue.lastName,
        staffId: inputValue.staffId,
        role: "superadmin",
        email: inputValue.email,
        password: inputValue.password,
      };

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/create-admin`,
          payload
        );
        toast(response.data.message);
        navigate("/");
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        console.error("An error occurred during registration:", error);
      }
    }
  };

  return (
    <div className="flex items-center justify-center h-[100vh]">
      <div className="px-4 lg:px-14 w-full lg:w-[60%] mt-16 lg:mt-[15rem]">
        <div className="flex items-center justify-center flex-col">
          <img src="assets/logo.png" alt="logo" className="pb-10 self-center" />
          <h1 className="font-bold text-xl mb-16">Create your account</h1>
        </div>
        <form
          className="px-4 lg:px-14 mb-[5rem]"
          onSubmit={handleCreateAccount}
        >
          <div className="flex flex-col mb-4">
            <label className="text-sm font-medium pb-1">First Name</label>
            <input
              type="text"
              name="firstName"
              value={inputValue.firstName}
              onChange={handleInputChange}
              placeholder="Jane"
              className="bg-gray-100 outline-none text-sm p-2 lg:p-3 rounded-md border border-gray-200"
            />
             {errors.firstName && (
              <p className="text-red-500 text-xs">{errors.firstName}</p>
            )}
          </div>
          <div className="flex flex-col mb-4">
            <label className="text-sm font-medium pb-1">Last Name</label>
            <input
              type="text"
              name="lastName"
              value={inputValue.lastName}
              onChange={handleInputChange}
              placeholder="Doe"
              className="bg-gray-100 outline-none text-sm p-2 lg:p-3 rounded-md border border-gray-200"
            />
             {errors.lastName && (
              <p className="text-red-500 text-xs">{errors.lastName}</p>
            )}
          </div>
          <div className="flex flex-col mb-4">
            <label className="text-sm font-medium pb-1">Email</label>
            <input
              name="email"
              value={inputValue.email}
              onChange={handleInputChange}
              type="text"
              placeholder="JaneDone@gmail.com"
              className="bg-gray-100 text-sm outline-none p-2 lg:p-3 rounded-md border border-gray-200"
            />
            {errors.email && (
              <p className="text-red-500 text-xs">{errors.email}</p>
            )}
          </div>
          <div className="flex flex-col mb-4">
            <label className="text-sm font-medium pb-1">Staff ID</label>
            <input
              name="staffId"
              value={inputValue.staffId}
              onChange={handleInputChange}
              type="text"
              placeholder="STIL/***/***"
              className="bg-gray-100 text-sm outline-none p-2 lg:p-3 rounded-md border border-gray-200"
            />
            {errors.staffId && (
              <p className="text-red-500 text-xs">{errors.staffId}</p>
            )}
          </div>
          <div className="flex flex-col mb-4">
            <label className="text-sm font-medium pb-1">Password</label>
            <input
              name="password"
              value={inputValue.password}
              onChange={handleInputChange}
              type="password"
              placeholder="***********"
              className="bg-gray-100 text-sm outline-none p-2 lg:p-3 rounded-md border border-gray-200"
            />
            {errors.password && (
              <p className="text-red-500 text-xs">{errors.password}</p>
            )}
          </div>
          <div className="flex flex-col mb-4">
            <label className="text-sm font-medium pb-1">Confirm Password</label>
            <input
              name="confirmPassword"
              value={inputValue.confirmPassword}
              onChange={handleInputChange}
              type="password"
              placeholder="************"
              className="bg-gray-100 text-sm outline-none p-2 lg:p-3 rounded-md border border-gray-200"
            />
            {errors.confirmPassword && (
              <p className="text-red-500 text-xs">{errors.confirmPassword}</p>
            )}
          </div>
          <button
            className="bg-secondary text-white w-[100%] text-base font-semibold p-2 lg:p-3 rounded-md shadow-sm mt-8 lg:mt-12 mb-6 transition-all duration-500 hover:border-secondary hover:border-2 hover:text-secondary hover:bg-transparent"
            type="submit"
          >
            {isLoading ? <LoadingSpinner /> : " Register"}
          </button>
          <p className="text-sm text-center">
            Already have an account?{" "}
            <a href="/">
              <span className="text-secondary underline">Login</span>
            </a>
          </p>
        </form>
      </div>
      {/* <div>
        <Banner />
      </div> */}
    </div>
  );
};

export default AdminSignup;
